import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import type { Keyframes } from 'styled-components';

import { DropdownBackground } from '@/components/base';
import Transition from '@/components/Common/Transition';
import Portal from '@/components/Portal/Portal';
import useColor from '@/hooks/domain/useColor';
import { animation } from '@/styles/style';
import zIndex from '@/styles/zIndex';

export type AnimationInOut = {
  in: Keyframes;
  out: Keyframes;
};

export type TransitionPortalProps = {
  status: boolean;
  children?: ReactNode;
  close?: (e?: MouseEvent) => void;
  onBgRightClick?: (e: MouseEvent) => void;
  style?: CSSProperties;
  layerId?: string;
  duration?: number;
  animations?: AnimationInOut;
  fallback?: ReactNode;
  background?: {
    enable?: boolean;
    style?: CSSProperties;
  };
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

const TransitionPortal = (props: TransitionPortalProps) => {
  const backgroundStyle = props.background?.style;
  const backgroundEnable =
    typeof props.background?.enable === 'undefined'
      ? true
      : props.background?.enable;
  const id = props.layerId || 'dropdown-layer';
  const delay = props.duration || 200;
  const color = useColor();
  return (
    <Portal id={id}>
      {backgroundEnable && (
        <Transition
          status={props.status}
          style={{
            position: 'fixed',
            zIndex: zIndex.dim,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            ...backgroundStyle,
          }}
          animations={{
            in: animation.fadeIn,
            out: animation.fadeOut,
          }}
        >
          <DropdownBackground
            onClick={(e) => {
              e.stopPropagation();
              props.close?.(e);
            }}
            onContextMenu={(e) => {
              e.stopPropagation();
              e.preventDefault();
              props.onBgRightClick?.(e);
            }}
          />
        </Transition>
      )}
      <Transition
        status={props.status}
        style={{
          zIndex: zIndex.dropdown,
          position: 'fixed',
          boxShadow: `0px 2px 16px ${color.shadow}`,
          backgroundColor: color.gray100,
          border: `1px solid ${color.gray300}`,
          backdropFilter: 'blur(12px)',
          borderRadius: 8,
          ...props.style,
        }}
        duration={delay}
        animations={props.animations}
        fallback={props.fallback}
        onClick={props.onClick}
      >
        {props.children}
      </Transition>
    </Portal>
  );
};

export default TransitionPortal;
